import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Badge } from 'react-bootstrap';
import { FaInfoCircle, FaPlus, FaTimes, FaGripVertical } from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DataApi from '../../api/DataApi';
import ObjectConfigApi from '../../api/ObjectConfigApi';
import SearchViewApi from '../../api/SearchViewApi';
import {
  getOperatorsForDataType,
  defaultOperatorForDataType,
  isPersistableFilterBinding,
  filterBindingRequiresSourceField,
  effectiveFilterBindingOperator
} from '../../utils/searchFilterOperators';

/** Keep in sync with layoutwidget.model.js / layoutwidget.routes.js search validation */
const MAX_SEARCH_WIDGET_DISPLAY_FIELDS = 60;

function uniqueFieldsPreserveOrder(fields) {
  const seen = new Set();
  return (Array.isArray(fields) ? fields : []).filter((f) => {
    if (f == null || f === '' || seen.has(f)) return false;
    seen.add(f);
    return true;
  });
}

function getAvailableWidgetPositions(totalColumns, detailColumnPosition) {
  const available = [];
  for (let i = 1; i <= totalColumns; i++) {
    if (i !== detailColumnPosition) available.push(i);
  }
  return available;
}

function getValidWidgetPosition(layoutMetadata, currentPosition) {
  const available = getAvailableWidgetPositions(
    layoutMetadata.total_columns,
    layoutMetadata.detail_column_position
  );
  let valid = currentPosition;
  if (!available.includes(valid) && available.length > 0) {
    valid = available[0];
  }
  return valid;
}

/**
 * Configure a layout search widget (create or edit). Same options as Manage Widgets.
 */
const SearchWidgetConfigModal = ({
  show,
  onHide,
  objectName,
  layoutId,
  layoutMetadata = { total_columns: 3, detail_column_position: 1 },
  displayOrderForCreate = 0,
  editingWidget = null,
  onSaved,
  /** When true (Related List Manager): show layout order instead of left/middle/right column picker */
  relatedListManagerContext = false
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [availableSearchViews, setAvailableSearchViews] = useState([]);
  const [sourceObjectFields, setSourceObjectFields] = useState([]);
  const [searchViewFields, setSearchViewFields] = useState([]);
  const [filterableFields, setFilterableFields] = useState([]);
  const [searchViewConfig, setSearchViewConfig] = useState(null);
  const [searchViewConfigsCache, setSearchViewConfigsCache] = useState({});
  const [configForm, setConfigForm] = useState({
    widget_label: 'Search Results',
    widget_column_position: 3,
    fields: [],
    maxRecords: 10,
    maxHeight: 400,
    search_view_id: '',
    search_view_name: '',
    filter_bindings: [],
    display_as_table: false
  });

  const refreshSearchViews = useCallback(async () => {
    try {
      const searchViewApi = new SearchViewApi();
      const response = await searchViewApi.fetchAll();
      if (response?.data?.success && Array.isArray(response.data.data)) {
        const searchViews = response.data.data;
        setAvailableSearchViews(
          searchViews.map((sv) => ({
            id: sv.id,
            object_name: sv.object_name,
            object_label: sv.object_label || sv.object_name,
            view_label: sv.view_label,
            search_view_name: sv.search_view_name,
            display_fields: sv.display_fields || [],
            filterable_fields: sv.filterable_fields || [],
            fullData: sv
          }))
        );
      } else {
        setAvailableSearchViews([]);
      }
    } catch (e) {
      console.error('[SearchWidgetConfigModal] refreshSearchViews:', e);
      setAvailableSearchViews([]);
    }
  }, []);

  useEffect(() => {
    if (!show || !objectName) return;

    (async () => {
      setError('');
      await refreshSearchViews();
      try {
        const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
        if (response?.data?.fields) {
          setSourceObjectFields(
            response.data.fields.map((f) => ({
              field_name: f.field_name,
              field_label: f.field_label || f.field_name,
              data_type: f.data_type
            }))
          );
        }
      } catch (e) {
        console.error('[SearchWidgetConfigModal] source fields:', e);
      }
    })();
  }, [show, objectName, refreshSearchViews]);

  useEffect(() => {
    if (!show) return;

    const positions = getAvailableWidgetPositions(
      layoutMetadata.total_columns,
      layoutMetadata.detail_column_position
    );
    const defaultPosition = positions.length > 0 ? positions[0] : 3;

    if (editingWidget) {
      const cfg = editingWidget.config || {};
      const widgetSearchViewId = cfg.search_view_id || '';
      const widgetFields = uniqueFieldsPreserveOrder(cfg.fields || []);
      const widgetFilterBindings = cfg.filter_bindings || [];
      if (widgetSearchViewId) {
        setSearchViewConfigsCache({
          [widgetSearchViewId]: {
            fields: widgetFields,
            filter_bindings: widgetFilterBindings
          }
        });
      } else {
        setSearchViewConfigsCache({});
      }
      setConfigForm({
        widget_label: editingWidget.widget_label || 'Search Results',
        widget_column_position: getValidWidgetPosition(
          layoutMetadata,
          editingWidget.widget_column_position
        ),
        fields: widgetFields,
        maxRecords: cfg.max_records || cfg.maxRecords || 10,
        maxHeight: cfg.max_height || cfg.maxHeight || 400,
        search_view_id: widgetSearchViewId,
        search_view_name: cfg.search_view_name || '',
        filter_bindings: widgetFilterBindings,
        display_as_table: cfg.display_as_table === true
      });
    } else {
      setSearchViewConfigsCache({});
      setConfigForm({
        widget_label: 'Search Results',
        widget_column_position: defaultPosition,
        fields: [],
        maxRecords: 10,
        maxHeight: 400,
        search_view_id: '',
        search_view_name: '',
        filter_bindings: [],
        display_as_table: false
      });
    }
  }, [show, editingWidget, layoutMetadata]);

  useEffect(() => {
    if (!configForm.search_view_id) {
      setSearchViewFields([]);
      setFilterableFields([]);
      setSearchViewConfig(null);
      return;
    }

    const selected = availableSearchViews.find(
      (sv) => String(sv.id) === String(configForm.search_view_id)
    );
    if (!selected) {
      setSearchViewFields([]);
      setFilterableFields([]);
      setSearchViewConfig(null);
      return;
    }

    const config = selected.fullData || selected;
    setSearchViewConfig(config);

    (async () => {
      const searchViewObjectName = config.object_name;
      let allFields = [];
      if (searchViewObjectName) {
        try {
          const objectConfigResponse = await ObjectConfigApi.fetchConfigObjectByNameWithFields(
            searchViewObjectName
          );
          allFields =
            objectConfigResponse?.data?.fields || objectConfigResponse?.fields || [];
        } catch (e) {
          console.warn('[SearchWidgetConfigModal] object config:', e);
        }
      }

      let displayFieldsArray = [];
      if (Array.isArray(config.display_fields)) {
        displayFieldsArray = config.display_fields;
      } else if (typeof config.display_fields === 'string') {
        try {
          displayFieldsArray = JSON.parse(config.display_fields);
        } catch {
          displayFieldsArray = config.display_fields
            .split(',')
            .map((f) => f.trim())
            .filter(Boolean);
        }
      }

      const displayFields = displayFieldsArray.map((fieldName) => {
        const fieldInfo = allFields.find((f) => f.field_name === fieldName);
        return {
          field_name: fieldName,
          field_label: fieldInfo?.field_label || fieldName,
          data_type: fieldInfo?.data_type || 'text'
        };
      });
      setSearchViewFields(displayFields);

      let filterableFieldsArray = [];
      if (Array.isArray(config.filterable_fields)) {
        filterableFieldsArray = config.filterable_fields;
      } else if (typeof config.filterable_fields === 'string') {
        try {
          filterableFieldsArray = JSON.parse(config.filterable_fields);
        } catch {
          filterableFieldsArray = config.filterable_fields
            .split(',')
            .map((f) => f.trim())
            .filter(Boolean);
        }
      }

      const filterFields = filterableFieldsArray.map((fieldName) => {
        const fieldInfo = allFields.find((f) => f.field_name === fieldName);
        return {
          field_name: fieldName,
          field_label: fieldInfo?.field_label || fieldName,
          data_type: fieldInfo?.data_type || 'text'
        };
      });
      setFilterableFields(filterFields);
    })();
  }, [configForm.search_view_id, availableSearchViews]);

  const handleSave = async () => {
    setError('');
    if (!layoutId) {
      setError('Layout ID is missing.');
      return;
    }
    if (!configForm.search_view_id) {
      setError('Please select a search view');
      return;
    }
    const selectedDisplayFields = uniqueFieldsPreserveOrder(
      (configForm.fields || []).filter(Boolean)
    );
    if (selectedDisplayFields.length === 0) {
      setError('Please select at least one field to display in the search widget');
      return;
    }
    if (selectedDisplayFields.length > MAX_SEARCH_WIDGET_DISPLAY_FIELDS) {
      setError(
        `Maximum ${MAX_SEARCH_WIDGET_DISPLAY_FIELDS} fields allowed for search widgets`
      );
      return;
    }
    const filterBindings = (configForm.filter_bindings || []).filter(isPersistableFilterBinding);

    try {
      setLoading(true);
      const api = new DataApi('layoutwidget');

      const configPayload = {
        search_view_id: configForm.search_view_id,
        search_view_name: configForm.search_view_name || undefined,
        filter_bindings: filterBindings,
        fields: selectedDisplayFields,
        max_records: configForm.maxRecords || 10,
        max_height: configForm.maxHeight || 400,
        display_as_table: configForm.display_as_table === true,
        // Related List Manager → detail tabs; Manage Widgets uses LayoutWidgetManager with false
        show_in_detail_tabs: relatedListManagerContext === true
      };

      if (editingWidget?.id) {
        const updateData = {
          widget_label: configForm.widget_label,
          widget_column_position: configForm.widget_column_position,
          config: configPayload
        };
        const response = await api.put(`/${editingWidget.id}`, updateData);
        if (response?.data?.success) {
          onSaved?.();
          onHide();
        } else {
          setError(response?.data?.message || 'Failed to update search widget');
        }
      } else {
        const widgetData = {
          layout_id: layoutId,
          widget_type: 'search',
          widget_label: configForm.widget_label || 'Search Results',
          widget_column_position: configForm.widget_column_position,
          component_id: null,
          config: configPayload,
          display_order: displayOrderForCreate,
          is_active: true
        };
        const response = await api.create(widgetData);
        if (response?.data?.success) {
          onSaved?.();
          onHide();
        } else {
          setError(response?.data?.message || 'Failed to add search widget');
        }
      }
    } catch (e) {
      console.error('[SearchWidgetConfigModal] save:', e);
      setError(e.message || 'Failed to save search widget');
    } finally {
      setLoading(false);
    }
  };

  const positions = getAvailableWidgetPositions(
    layoutMetadata.total_columns,
    layoutMetadata.detail_column_position
  );
  const positionLabels = {
    1: 'Left (Column 1)',
    2: 'Middle (Column 2)',
    3: 'Right/Side (Column 3)'
  };

  const columnOrderShown =
    editingWidget != null && editingWidget.display_order != null
      ? Number(editingWidget.display_order)
      : displayOrderForCreate;

  const selectedFieldNames = configForm.fields || [];
  const maxSearchDisplayFields = Math.min(
    searchViewFields.length,
    MAX_SEARCH_WIDGET_DISPLAY_FIELDS
  );
  const availableFieldsForPicker = searchViewFields.filter(
    (f) => !selectedFieldNames.includes(f.field_name)
  );

  const addDisplayField = (fieldName) => {
    if (selectedFieldNames.length >= maxSearchDisplayFields) {
      setError(
        `Maximum ${maxSearchDisplayFields} fields allowed (limited by this search view and system cap)`
      );
      return;
    }
    if (!fieldName || selectedFieldNames.includes(fieldName)) return;
    setConfigForm({
      ...configForm,
      fields: [...selectedFieldNames, fieldName]
    });
    setError('');
  };

  const removeDisplayField = (fieldName) => {
    setConfigForm({
      ...configForm,
      fields: selectedFieldNames.filter((f) => f !== fieldName)
    });
  };

  const handleSelectedFieldsDragEnd = (result) => {
    if (!result.destination) return;
    if (
      result.source.droppableId !== 'sw-selected-fields' ||
      result.destination.droppableId !== 'sw-selected-fields'
    ) {
      return;
    }
    const next = Array.from(selectedFieldNames);
    const [removed] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, removed);
    setConfigForm({ ...configForm, fields: next });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{editingWidget ? 'Edit search widget' : 'Add search widget'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        <Form onSubmit={(e) => e.preventDefault()}>
          <Form.Group className="mb-3">
            <Form.Label>Widget label</Form.Label>
            <Form.Control
              type="text"
              value={configForm.widget_label}
              onChange={(e) => setConfigForm({ ...configForm, widget_label: e.target.value })}
              placeholder="Search Results"
            />
          </Form.Group>

          {relatedListManagerContext ? (
            <Form.Group className="mb-3">
              <Form.Label>Column order</Form.Label>
              <Form.Control type="text" readOnly disabled value={String(columnOrderShown)} />
              <Form.Text className="text-muted">
                Order in the layout list (related lists and search widgets). Drag rows in Layout related lists and search to change it.
              </Form.Text>
            </Form.Group>
          ) : (
            <Form.Group className="mb-3">
              <Form.Label>Column position</Form.Label>
              <Form.Select
                value={configForm.widget_column_position}
                onChange={(e) =>
                  setConfigForm({
                    ...configForm,
                    widget_column_position: parseInt(e.target.value, 10)
                  })
                }
              >
                {positions.map((pos) => (
                  <option key={pos} value={pos}>
                    {positionLabels[pos] || `Column ${pos}`}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Detail uses column {layoutMetadata.detail_column_position}. Widgets use the other columns.
              </Form.Text>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Search view</Form.Label>
            <Form.Select
              value={configForm.search_view_id}
              onChange={(e) => {
                const newSearchViewId = e.target.value;
                const previousSearchViewId = configForm.search_view_id;
                if (previousSearchViewId) {
                  setSearchViewConfigsCache((prev) => ({
                    ...prev,
                    [previousSearchViewId]: {
                      fields: configForm.fields || [],
                      filter_bindings: configForm.filter_bindings || []
                    }
                  }));
                }
                const cachedConfig = searchViewConfigsCache[newSearchViewId];
                const picked = availableSearchViews.find((s) => String(s.id) === String(newSearchViewId));
                setConfigForm({
                  ...configForm,
                  search_view_id: newSearchViewId,
                  search_view_name: picked?.search_view_name || '',
                  fields: cachedConfig?.fields || [],
                  filter_bindings: cachedConfig?.filter_bindings || []
                });
              }}
            >
              <option value="">Select a search view...</option>
              {availableSearchViews.map((sv) => {
                const objTitle = sv.object_label || sv.object_name || '';
                const viewLbl = sv.view_label || '—';
                const slug = sv.search_view_name || '—';
                return (
                  <option key={sv.id} value={sv.id}>
                    {`${objTitle} (${viewLbl} - ${slug})`}
                  </option>
                );
              })}
            </Form.Select>
            <Form.Text className="text-muted">Configured under Setup → Search Views.</Form.Text>
          </Form.Group>

          {configForm.search_view_id && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Fields to display</Form.Label>
                <Form.Text className="text-muted d-block mb-2">
                  Choose fields to show in this search widget (up to {maxSearchDisplayFields} for this view, max {MAX_SEARCH_WIDGET_DISPLAY_FIELDS} overall). Add from Available, remove from Selected; drag Selected to reorder.
                </Form.Text>
                {configForm.search_view_id &&
                  searchViewFields.length === 0 &&
                  filterableFields.length === 0 &&
                  !searchViewConfig && (
                    <p className="text-muted">Loading fields from search view...</p>
                  )}
                {searchViewFields.length > 0 && (
                  <Row className="g-3">
                    <Col md={6}>
                      <div
                        className="border rounded p-3 h-100"
                        style={{ minHeight: 220, backgroundColor: '#fff' }}
                      >
                        <div className="fw-semibold mb-1">Available fields</div>
                        <small className="text-muted d-block mb-2">
                          All fields from this search view ({searchViewFields.length}). Not yet shown in the widget.
                        </small>
                        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                          {availableFieldsForPicker.length === 0 ? (
                            <p className="text-muted small mb-0">
                              {selectedFieldNames.length >= maxSearchDisplayFields
                                ? `Maximum ${maxSearchDisplayFields} fields selected. Remove one to add another.`
                                : 'Every display field is already selected.'}
                            </p>
                          ) : (
                            availableFieldsForPicker.map((field) => (
                              <div
                                key={field.field_name}
                                className="d-flex justify-content-between align-items-center py-2 border-bottom"
                              >
                                <div className="me-2" style={{ minWidth: 0 }}>
                                  <div className="text-truncate">{field.field_label}</div>
                                  <small className="text-muted">{field.field_name}</small>
                                </div>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline-primary"
                                  className="flex-shrink-0"
                                  title="Add to selected"
                                  disabled={selectedFieldNames.length >= maxSearchDisplayFields}
                                  onClick={() => addDisplayField(field.field_name)}
                                >
                                  <FaPlus />
                                </Button>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div
                        className="border rounded p-3 h-100"
                        style={{ minHeight: 220, backgroundColor: '#f8f9fa' }}
                      >
                        <div className="fw-semibold mb-1">
                          Selected fields ({selectedFieldNames.length}/{maxSearchDisplayFields || MAX_SEARCH_WIDGET_DISPLAY_FIELDS})
                        </div>
                        <small className="text-muted d-block mb-2">
                          Shown on the widget in this order. Drag the grip to reorder.
                        </small>
                        {selectedFieldNames.length === 0 ? (
                          <p className="text-muted small mb-0">None selected yet. Add fields from the left.</p>
                        ) : (
                          <DragDropContext onDragEnd={handleSelectedFieldsDragEnd}>
                            <Droppable droppableId="sw-selected-fields">
                              {(dropProvided) => (
                                <div
                                  ref={dropProvided.innerRef}
                                  {...dropProvided.droppableProps}
                                  style={{ minHeight: 48 }}
                                >
                                  {selectedFieldNames.map((fieldName, index) => {
                                    const fieldMeta = searchViewFields.find(
                                      (f) => f.field_name === fieldName
                                    );
                                    const label = fieldMeta?.field_label || fieldName;
                                    return (
                                      <Draggable
                                        key={fieldName}
                                        draggableId={`sw-sel-${fieldName}`}
                                        index={index}
                                      >
                                        {(dragProvided, snapshot) => (
                                          <div
                                            ref={dragProvided.innerRef}
                                            {...dragProvided.draggableProps}
                                            className={`d-flex align-items-center gap-2 mb-2 p-2 bg-white border rounded ${
                                              snapshot.isDragging ? 'shadow-sm' : ''
                                            }`}
                                          >
                                            <div
                                              {...dragProvided.dragHandleProps}
                                              className="text-muted"
                                              style={{ cursor: 'grab' }}
                                              title="Drag to reorder"
                                            >
                                              <FaGripVertical />
                                            </div>
                                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                              <div className="text-truncate">{label}</div>
                                              <small className="text-muted">{fieldName}</small>
                                            </div>
                                            <Button
                                              type="button"
                                              variant="link"
                                              className="p-0 text-danger flex-shrink-0"
                                              title="Remove"
                                              onClick={() => removeDisplayField(fieldName)}
                                            >
                                              <FaTimes />
                                            </Button>
                                          </div>
                                        )}
                                      </Draggable>
                                    );
                                  })}
                                  {dropProvided.placeholder}
                                </div>
                              )}
                            </Droppable>
                          </DragDropContext>
                        )}
                      </div>
                    </Col>
                  </Row>
                )}
                {searchViewConfig && searchViewFields.length === 0 && (
                  <Alert variant="warning" className="mb-0">
                    <FaInfoCircle className="me-2" />
                    No display fields configured in this search view.
                  </Alert>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Filter bindings</Form.Label>
                <Form.Text className="text-muted d-block mb-2">
                  Map search filters to fields on this record ({objectName})
                </Form.Text>
                {filterableFields.length > 0 ? (
                  filterableFields.map((filterField) => {
                    const binding =
                      (configForm.filter_bindings || []).find(
                        (b) => b.filterable_field === filterField.field_name
                      ) || {
                        filterable_field: filterField.field_name,
                        source_field: '',
                        operator: defaultOperatorForDataType(filterField.data_type)
                      };
                    const opValue = effectiveFilterBindingOperator(binding);
                    const operatorOpts = getOperatorsForDataType(filterField.data_type);
                    return (
                      <Row key={filterField.field_name} className="mb-2 align-items-end">
                        <Col md={4} className="mb-2 mb-md-0">
                          <strong>{filterField.field_label}</strong>
                          <br />
                          <small className="text-muted">({filterField.field_name})</small>
                        </Col>
                        <Col md={3} className="mb-2 mb-md-0">
                          <Form.Label className="small text-muted mb-1">Operator</Form.Label>
                          <Form.Select
                            value={opValue}
                            onChange={(e) => {
                              const newOp = e.target.value;
                              const newBindings = [...(configForm.filter_bindings || [])];
                              const ix = newBindings.findIndex(
                                (b) => b.filterable_field === filterField.field_name
                              );
                              const patch = {
                                filterable_field: filterField.field_name,
                                source_field: binding.source_field || '',
                                operator: newOp
                              };
                              if (!filterBindingRequiresSourceField(newOp)) {
                                patch.source_field = '';
                              }
                              if (ix >= 0) {
                                newBindings[ix] = { ...newBindings[ix], ...patch };
                              } else {
                                newBindings.push(patch);
                              }
                              setConfigForm({ ...configForm, filter_bindings: newBindings });
                            }}
                          >
                            {operatorOpts.map((op) => (
                              <option key={op.value} value={op.value}>
                                {op.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col md={4} className="mb-2 mb-md-0">
                          <Form.Label className="small text-muted mb-1">Current record field</Form.Label>
                          <Form.Select
                            value={binding.source_field || ''}
                            disabled={!filterBindingRequiresSourceField(opValue)}
                            onChange={(e) => {
                              const newBindings = [...(configForm.filter_bindings || [])];
                              const ix = newBindings.findIndex(
                                (b) => b.filterable_field === filterField.field_name
                              );
                              if (ix >= 0) {
                                newBindings[ix] = {
                                  ...newBindings[ix],
                                  filterable_field: filterField.field_name,
                                  source_field: e.target.value,
                                  operator: opValue
                                };
                              } else {
                                newBindings.push({
                                  filterable_field: filterField.field_name,
                                  source_field: e.target.value,
                                  operator: opValue
                                });
                              }
                              setConfigForm({ ...configForm, filter_bindings: newBindings });
                            }}
                          >
                            <option value="">
                              {filterBindingRequiresSourceField(opValue)
                                ? 'Select field from current record...'
                                : 'Not required'}
                            </option>
                            {sourceObjectFields.map((f) => (
                              <option key={f.field_name} value={f.field_name}>
                                {f.field_label} ({`{{${objectName}.${f.field_name}}}`})
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col md={1} className="text-md-center mb-2 mb-md-0">
                          {isPersistableFilterBinding({
                            filterable_field: filterField.field_name,
                            source_field: binding.source_field,
                            operator: opValue
                          }) ? (
                            <Badge bg="success">Bound</Badge>
                          ) : null}
                        </Col>
                      </Row>
                    );
                  })
                ) : (
                  <Alert variant="info" className="mb-0">
                    No filterable fields on this search view. All matching records will be shown.
                  </Alert>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Max records</Form.Label>
                <Form.Control
                  type="number"
                  value={configForm.maxRecords}
                  onChange={(e) =>
                    setConfigForm({
                      ...configForm,
                      maxRecords: parseInt(e.target.value, 10) || 10
                    })
                  }
                  min={1}
                  max={50}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Max height (px)</Form.Label>
                <Form.Control
                  type="number"
                  min={200}
                  max={1000}
                  step={50}
                  value={configForm.maxHeight}
                  onChange={(e) =>
                    setConfigForm({
                      ...configForm,
                      maxHeight: parseInt(e.target.value, 10) || 400
                    })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="sw-modal-display-table"
                  label="Show results as a table (list view row actions)"
                  checked={configForm.display_as_table === true}
                  onChange={(e) =>
                    setConfigForm({ ...configForm, display_as_table: e.target.checked })
                  }
                />
              </Form.Group>

              <Alert variant="info">
                <FaInfoCircle className="me-2" />
                Results use the selected search view; bindings filter rows using the open record.
              </Alert>
            </>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSave}
          disabled={loading || !configForm.search_view_id}
        >
          {loading ? 'Saving...' : editingWidget ? 'Update' : 'Add widget'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SearchWidgetConfigModal;
